import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { HandCoins, Loader2, AlertTriangle } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { listOutstandingInvoices, allocateVoucherCredit } from '@/app/services/paymentService';
import { OutstandingInvoice, AllocateVoucherCreditRequest } from '@/app/types/payment';

interface AllocateCreditModalProps {
  isOpen: boolean;
  onClose: () => void;
  paymentVoucherId: number;
  voucherNumber: string;
  availableCredit: number;
  partyId: number;
  partyName: string;
  onSuccess: () => void;
}

interface AllocationRow {
  invoiceId: number;
  amount: string;
}

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const formatDate = (dateString: string): string => {
  try {
    return format(new Date(dateString), 'dd/MM/yyyy');
  } catch {
    return dateString;
  }
};

export function AllocateCreditModal({
  isOpen,
  onClose,
  paymentVoucherId,
  voucherNumber,
  availableCredit,
  partyId,
  partyName,
  onSuccess
}: AllocateCreditModalProps) {
  const [allocations, setAllocations] = useState<AllocationRow[]>([]);
  const [error, setError] = useState('');

  // Fetch outstanding invoices
  const { data: invoices = [], isLoading: isLoadingInvoices } = useQuery<OutstandingInvoice[]>({
    queryKey: ['outstandingInvoices', partyId],
    queryFn: () => listOutstandingInvoices(partyId),
    enabled: isOpen && !!partyId,
  });

  // Allocate credit mutation
  const allocateMutation = useMutation({
    mutationFn: (data: AllocateVoucherCreditRequest) => allocateVoucherCredit(data),
    onSuccess: () => {
      toast.success('Credit allocated successfully');
      onSuccess();
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to allocate credit';
      toast.error(errorMsg);
    },
  });

  // Initialize allocations when invoices are loaded or modal opens
  useEffect(() => {
    if (isOpen && invoices.length > 0) {
      setAllocations(invoices.map(invoice => ({
        invoiceId: invoice.id,
        amount: ''
      })));
    }
  }, [isOpen, invoices]);

  // Calculate total allocated
  const totalAllocated = allocations.reduce((sum, alloc) => {
    const amount = parseFloat(alloc.amount) || 0;
    return sum + amount;
  }, 0);

  const remainingCredit = availableCredit - totalAllocated;

  const handleAmountChange = (invoiceId: number, value: string) => {
    // Allow only numbers and decimal point
    if (value && !/^\d*\.?\d*$/.test(value)) return;

    // Find the invoice to check amount due
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) return;

    const numValue = parseFloat(value) || 0;
    
    // Check if allocation exceeds amount due
    if (numValue > invoice.amount_due) {
      setError(`Allocation for ${invoice.voucher_number} cannot exceed amount due (${formatCurrency(invoice.amount_due)})`);
      return;
    }

    setAllocations(prev => 
      prev.map(alloc => 
        alloc.invoiceId === invoiceId 
          ? { ...alloc, amount: value }
          : alloc
      )
    );
    setError('');
  };

  const handleAllocate = () => {
    // Validation
    if (totalAllocated === 0) {
      setError('Please enter allocation amounts');
      return;
    }

    if (totalAllocated > availableCredit) {
      setError(`Total allocation (${formatCurrency(totalAllocated)}) exceeds available credit (${formatCurrency(availableCredit)})`);
      return;
    }

    // Check individual allocations don't exceed invoice amounts
    for (const alloc of allocations) {
      if (parseFloat(alloc.amount) > 0) {
        const invoice = invoices.find(inv => inv.id === alloc.invoiceId);
        if (invoice && parseFloat(alloc.amount) > invoice.amount_due) {
          setError(`Allocation for ${invoice.voucher_number} exceeds amount due`);
          return;
        }
      }
    }

    // Prepare allocations
    const validAllocations = allocations
      .filter(alloc => parseFloat(alloc.amount) > 0)
      .map(alloc => ({
        invoice_voucher_id: alloc.invoiceId,
        amount_to_allocate: parseFloat(alloc.amount)
      }));

    // Submit
    allocateMutation.mutate({
      payment_voucher_id: paymentVoucherId,
      allocations: validAllocations,
      narration: `Allocating credit from payment ${voucherNumber}`
    });
  };

  const handleClose = () => {
    setAllocations([]);
    setError('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HandCoins className="h-5 w-5" />
            Allocate Credit from {voucherNumber}
          </DialogTitle>
          <DialogDescription>
            Available Credit: <span className="font-semibold text-warning">{formatCurrency(availableCredit)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isLoadingInvoices ? (
            <div className="flex justify-center items-center py-8">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              <span>Loading outstanding invoices...</span>
            </div>
          ) : invoices.length === 0 ? (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                No outstanding invoices found for {partyName}.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              <div className="rounded-md border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Invoice #</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Date</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Amount Due</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Allocate</th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {invoices.map((invoice, index) => {
                      const allocation = allocations.find(a => a.invoiceId === invoice.id);
                      return (
                        <tr key={invoice.id}>
                          <td className="px-4 py-3 text-sm">{invoice.voucher_number}</td>
                          <td className="px-4 py-3 text-sm">{formatDate(invoice.transaction_date)}</td>
                          <td className="px-4 py-3 text-sm text-right font-medium">
                            {formatCurrency(invoice.amount_due)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-2">
                              <Input
                                type="text"
                                value={allocation?.amount || ''}
                                onChange={(e) => handleAmountChange(invoice.id, e.target.value)}
                                placeholder="0.00"
                                className="w-32 text-right"
                                disabled={allocateMutation.isPending}
                                max={invoice.amount_due}
                              />
                              {allocation?.amount && parseFloat(allocation.amount) > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  / {formatCurrency(invoice.amount_due)}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 border-t">
                      <td colSpan={3} className="px-4 py-3 text-right text-sm font-medium">
                        Total to Allocate:
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${totalAllocated > availableCredit ? 'text-destructive' : ''}`}>
                          {formatCurrency(totalAllocated)}
                        </span>
                      </td>
                    </tr>
                    {totalAllocated > 0 && (
                      <tr className="bg-muted/50">
                        <td colSpan={3} className="px-4 py-2 text-right text-sm">
                          Remaining Credit:
                        </td>
                        <td className="px-4 py-2 text-right text-sm">
                          {formatCurrency(remainingCredit)}
                        </td>
                      </tr>
                    )}
                  </tfoot>
                </table>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={allocateMutation.isPending}>
            Cancel
          </Button>
          <Button 
            onClick={handleAllocate}
            disabled={allocateMutation.isPending || isLoadingInvoices || invoices.length === 0 || totalAllocated === 0}
          >
            {allocateMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Allocating...
              </>
            ) : (
              'Allocate'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}